import { ChangeEvent, JSX, MouseEvent, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import { Close, CurrencyExchange } from '@mui/icons-material';
import { useNotify, useRecordContext } from 'react-admin';
import { SubscriptionInvoice } from "../../../types/entities";
import { convertCurrencyToSymbol } from "../../../defaults/defaults";
import { RefundResult, useInvoiceRefund } from "./hooks/susbcription-invoice-refund-block.hooks";

type RefundType = 'all' | 'partial';

/** Money compared in pence, as HQ-API compares it, so the two cannot disagree over a rounding. */
function toPence(value: number): number {
    return Math.round(value * 100);
}

/**
 * Refunding an invoice, from its show page.
 *
 * Two steps on purpose: the first press shows the exact amount that will leave the account and
 * the second sends it. Changing anything after the first press goes back to the first step, so
 * what was confirmed is always what is sent.
 */
export const SubscriptionInvoiceRefundBlock: () => JSX.Element = (): JSX.Element => {
    const invoice: SubscriptionInvoice | undefined = useRecordContext<SubscriptionInvoice>();
    const { isRefunding, refundError, processRefund } = useInvoiceRefund();
    const notify = useNotify();
    const [open, setOpen] = useState<boolean>(false);
    const [refundType, setRefundType] = useState<RefundType>('all');
    const [amount, setAmount] = useState<string>('');
    const [notes, setNotes] = useState<string>('');
    const [confirming, setConfirming] = useState<boolean>(false);
    const [amountError, setAmountError] = useState<string>('');

    const symbol: string = convertCurrencyToSymbol(invoice?.currency ?? '');
    const amountPaid: number = parseFloat(invoice?.amountPaid ?? '') || 0;
    const refundValue: number = refundType === 'all' ? amountPaid : (parseFloat(amount) || 0);
    const refundLabel: string = symbol + refundValue.toFixed(2);

    function reset(): void {
        setRefundType('all');
        setAmount('');
        setNotes('');
        setConfirming(false);
        setAmountError('');
    }

    function handleClose(): void {
        if (isRefunding) {
            return;
        }
        setOpen(false);
        reset();
    }

    function handleTypeChange(_event: MouseEvent<HTMLElement>, value: RefundType | null): void {
        if (value !== null) {
            setRefundType(value);
            setConfirming(false);
            setAmountError('');
        }
    }

    function handleAmountChange(e: ChangeEvent<HTMLInputElement>): void {
        setAmount(e.target.value);
        setConfirming(false);
        setAmountError('');
    }

    function handleNotesChange(e: ChangeEvent<HTMLInputElement>): void {
        setNotes(e.target.value);
        setConfirming(false);
    }

    function review(): void {
        if (refundType === 'partial') {
            if (toPence(refundValue) <= 0) {
                setAmountError('Enter an amount to refund');
                return;
            }
            if (toPence(refundValue) > toPence(amountPaid)) {
                setAmountError('The refund cannot be more than the ' + symbol + amountPaid.toFixed(2) + ' paid');
                return;
            }
        }
        setConfirming(true);
    }

    async function submit(): Promise<void> {
        if (invoice === undefined) {
            return;
        }
        const result: RefundResult = await processRefund(
            invoice.id,
            refundType,
            refundType === 'all' ? 0 : refundValue,
            notes
        );
        if (result.status === 'success') {
            notify(result.message, { type: 'success' });
            setOpen(false);
            reset();
        }
    }

    return (
        <>
            <Button variant="outlined"
                    color="error"
                    startIcon={ <CurrencyExchange /> }
                    onClick={ (): void => setOpen(true) }
            >
                { "Refund Invoice" }
            </Button>
            <Dialog open={ open }
                    onClose={ handleClose }
                    fullWidth
                    maxWidth="sm"
                    slotProps={ { paper: { sx: { borderRadius: '14px' } } } }
            >
                <DialogTitle component="div"
                             sx={ {
                                 display: 'flex',
                                 alignItems: 'flex-start',
                                 justifyContent: 'space-between',
                                 gap: '16px',
                                 borderBottom: '1px solid',
                                 borderBottomColor: 'divider',
                                 paddingBottom: '14px',
                             } }
                >
                    <Box sx={ { minWidth: 0 } }>
                        <Typography component="h2" sx={ { fontSize: '18px', fontWeight: 700, lineHeight: 1.3 } }>
                            { "Refund invoice" }
                        </Typography>
                        <Typography sx={ {
                            fontSize: 'var(--wf-eyebrow)',
                            letterSpacing: 'var(--wf-track)',
                            textTransform: 'uppercase',
                            color: 'text.secondary',
                            fontWeight: 700,
                            marginTop: '4px',
                        } }>
                            { "Invoice #" + (invoice?.id ?? '') + ", " + symbol + amountPaid.toFixed(2) + " paid" }
                        </Typography>
                    </Box>
                    <IconButton onClick={ handleClose } aria-label="Close" size="small" disabled={ isRefunding }>
                        <Close fontSize="small" />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={ { display: 'flex', flexDirection: 'column', gap: '20px', paddingTop: '20px !important' } }>
                    <ToggleButtonGroup value={ refundType }
                                       exclusive
                                       fullWidth
                                       size="small"
                                       color="primary"
                                       disabled={ isRefunding }
                                       onChange={ handleTypeChange }
                                       aria-label="Refund type"
                    >
                        <ToggleButton value="all">{ "Full refund" }</ToggleButton>
                        <ToggleButton value="partial">{ "Partial refund" }</ToggleButton>
                    </ToggleButtonGroup>

                    { (refundType === 'partial') &&
                        <TextField label="Amount to refund"
                                   type="number"
                                   value={ amount }
                                   onChange={ handleAmountChange }
                                   disabled={ isRefunding }
                                   error={ amountError !== '' }
                                   helperText={ amountError || 'Up to ' + symbol + amountPaid.toFixed(2) }
                                   autoFocus
                                   fullWidth
                                   slotProps={ {
                                       input: { startAdornment: <InputAdornment position="start">{ symbol }</InputAdornment> },
                                       htmlInput: { min: 0, step: 0.01 },
                                   } }
                        />
                    }

                    <TextField label="Reason for the refund"
                               placeholder="Why is this invoice being refunded?"
                               helperText="Optional. Kept with the refund in HQ, and not sent to Stripe or the customer."
                               value={ notes }
                               onChange={ handleNotesChange }
                               disabled={ isRefunding }
                               multiline
                               minRows={ 3 }
                               maxRows={ 8 }
                               fullWidth
                    />

                    { (confirming) &&
                        <Alert severity="warning">
                            { "This refunds " + refundLabel + " to the customer through Stripe. It cannot be undone." }
                        </Alert>
                    }
                    { (refundError !== '') &&
                        <Alert severity="error">{ refundError }</Alert>
                    }
                </DialogContent>

                <DialogActions sx={ { padding: '12px 24px 20px 24px', gap: '8px' } }>
                    <Button onClick={ handleClose } disabled={ isRefunding }>
                        { "Cancel" }
                    </Button>
                    { (!confirming) &&
                        <Button variant="contained" onClick={ review }>
                            { "Review refund" }
                        </Button>
                    }
                    { (confirming) &&
                        <Button variant="contained"
                                color="error"
                                disabled={ isRefunding }
                                startIcon={ isRefunding ? <CircularProgress size={ 16 } color="inherit" /> : undefined }
                                onClick={ (): void => { void submit(); } }
                        >
                            { "Refund " + refundLabel }
                        </Button>
                    }
                </DialogActions>
            </Dialog>
        </>
    );
}
